import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Bell, Settings as SettingsIcon, Plus, Activity, ChevronLeft, ChevronRight, RefreshCw, Sun, Moon } from "lucide-react";
import { Button } from "./ui/button";
import TopTicker from "./TopTicker";

export default function Layout({ children, onAddSetup, onStatusUpdate, sidebarOpen, onSidebarToggle, theme = "dark", onToggleTheme }) {
  const location = useLocation();

  const navItem = (to, icon, label, testId) => {
    const Icon = icon;
    const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    return (
      <NavLink
        to={to}
        data-testid={testId}
        title={label}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
          active ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
        }`}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
        {sidebarOpen && <span className="font-display font-medium text-sm">{label}</span>}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-app text-app flex flex-col">
      {/* Top bar */}
      <header className="h-14 border-b border-zinc-800 px-4 flex items-center justify-between bg-app sticky top-0 z-30 gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={onSidebarToggle}
            className="p-1.5 hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="btn-sidebar-toggle"
            title={sidebarOpen ? "Sidebar einklappen" : "Sidebar ausklappen"}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-display font-black text-sm">F</div>
            <div className="font-display font-bold tracking-tight text-base whitespace-nowrap">
              FRIEDRICH<span className="text-zinc-500">·TRADE</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 ml-2">
            <Activity size={12} className="text-emerald-500" />
            <span className="font-mono-num">LIVE</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onToggleTheme}
            data-testid="btn-theme-toggle"
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
            className="h-9 w-9 flex items-center justify-center border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors text-zinc-300"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <Button
            data-testid="header-status-update-btn"
            onClick={onStatusUpdate}
            variant="outline"
            className="border-emerald-600/40 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-3"
          >
            <RefreshCw size={14} strokeWidth={2.5} className="mr-1" />
            <span className="hidden sm:inline">Status Update</span>
            <span className="sm:hidden">Status</span>
          </Button>
          <Button
            data-testid="header-add-setup-btn"
            onClick={onAddSetup}
            className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-4"
          >
            <Plus size={14} strokeWidth={2.5} className="mr-1" />
            Setup
          </Button>
        </div>
      </header>

      {/* Live ticker bar */}
      <TopTicker />

      <div className="flex-1 flex">
        {/* Sidebar */}
        <aside
          className={`border-r border-zinc-800 flex flex-col p-2 gap-1 transition-all duration-200 ${
            sidebarOpen ? "w-48" : "w-14"
          } shrink-0`}
        >
          {navItem("/", LayoutDashboard, "Dashboard", "nav-dashboard")}
          {navItem("/alerts", Bell, "Alerts", "nav-alerts")}
          {navItem("/settings", SettingsIcon, "Einstellungen", "nav-settings")}

          <div className="mt-auto pt-4">
            {sidebarOpen && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-display px-3 mb-1">
                  Powered by
                </div>
                <div className="text-[10px] text-zinc-500 font-mono-num px-3">Claude Sonnet 4.5</div>
              </>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
